import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getScrollers } from './content';
import logo from './logo.png'  // eslint-disable-line no-unused-vars

const ResponsiveMenu = ({ isMobile }) => {
  const [isOpen, setIsOpen] = useState(false);
    const navRef = useRef(null);
  const { pathname } = useLocation();

  // The hamburger bars take their tone from whatever is actually behind them.
  //
  // Two earlier attempts failed: one flipped at a single fixed scroll point,
  // which cannot work on a page of many bands, and one relied on every section
  // being tagged by hand, which breaks the moment a section is missed.
  //
  // This reads the real background colour under the button and measures its
  // brightness. A data-tone attribute still wins if one is present, but
  // nothing depends on it.
  const [barTone, setBarTone] = useState('light');

  useEffect(() => {
    let queued = false;

    const brightness = (colour) => {
      const m = colour && colour.match(/rgba?\(([^)]+)\)/);
      if (!m) return null;
      const parts = m[1].split(',').map((n) => parseFloat(n));
      if (parts.length > 3 && parts[3] < 0.5) return null;   // see-through
      const [r, g, b] = parts;
      return (r * 299 + g * 587 + b * 114) / 1000;           // 0 dark, 255 light
    };

    const read = () => {
      queued = false;
      const x = Math.max(40, window.innerWidth * 0.86);
      const stack = document.elementsFromPoint(x, 34) || [];

      for (const el of stack) {
        if (!(el instanceof HTMLElement)) continue;

        if (el.dataset && el.dataset.tone) {
          setBarTone(el.dataset.tone === 'dark' ? 'light' : 'dark');
          return;
        }

        const cs = window.getComputedStyle(el);
        // video, or a photograph used as a background: assume dark
        if (el.tagName === 'VIDEO' || el.tagName === 'IMG' ||
            (cs.backgroundImage && cs.backgroundImage !== 'none')) {
          setBarTone('light');
          return;
        }
        const lum = brightness(cs.backgroundColor);
        if (lum !== null) {
          setBarTone(lum < 150 ? 'light' : 'dark');   // light bars on a dark band
          return;
        }
      }
      setBarTone('dark');
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(read);
    };

    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname]);

  const toggleMenu = () => {
    const next = !isOpen;
    setIsOpen(next);
    // Let anything playing on the page know the menu has covered it.
    if (next) {
      window.dispatchEvent(new CustomEvent('menu:open'));
    }
  };

  // Close when clicking anywhere outside the panel, or on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointer = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const menuItems = [
    { label: 'Watch Film', href: '/#watch' },
    { label: 'Screenings', href: '/screenings' },
    { label: 'Be Aware', href: '/climate-awareness' },
    { label: 'Climate Change', href: '/climate-change' },
    { label: 'Contact', href: '/#contact' },
  ];

  return (
    <nav ref={navRef} className={`responsive-menu ${isMobile ? 'mobile' : ''} ${isOpen ? 'open' : ''} bars-${barTone}`}>
      <button className="hamburger-button" onClick={toggleMenu} aria-label={isOpen ? 'Close menu' : 'Open menu'} aria-expanded={isOpen}>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>
      {isOpen && (
        <div className="full-screen-menu">
          <div className='menu-logo'>
            <a href='/' onClick={() => setIsOpen(false)} aria-label='The Girl and Her Piano - home'>
              <img src='/assets/gap/title-blue.png' alt='The Girl and Her Piano' />
            </a>
          </div>
          <ul className="menu-items">
            {menuItems.map((item, index) => (
              <li key={index}>
                <a href={item.href} onClick={() => setIsOpen(false)}>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
          <div className='social-icons'>
          <a href='mailto:info@thegirlandherpiano.com' aria-label='Email' className='social-icon'>
            <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'></path>
              <polyline points='22,6 12,13 2,6'></polyline>
            </svg>
          </a>
          <a href='https://www.imdb.com/title/tt37798371/' target='_blank' rel='noopener noreferrer' aria-label='IMDb' className='social-icon social-icon--imdb'>IMDb</a>
          <a href='https://www.instagram.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' aria-label='Instagram' className='social-icon'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <rect x='2' y='2' width='20' height='20' rx='5' ry='5'></rect>
                <path d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'></path>
                <line x1='17.5' y1='6.5' x2='17.51' y2='6.5'></line>
              </svg>
            </a>
            <a href='https://www.youtube.com/@hillaryosullivan' target='_blank' rel='noopener noreferrer' aria-label='YouTube' className='social-icon social-icon--youtube'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6z'></path>
              </svg>
            </a>
            <a href='https://www.facebook.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' aria-label='Facebook' className='social-icon'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor' stroke='currentColor' strokeWidth='0.5' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z'></path>
              </svg>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

function App() {
  const dispatch = useDispatch();

  const [isMobile, setIsMobile] = useState(false)
  const [isLandscape, setIsLandscape] = useState(false)
  const [isSmallScreen, setIsSmallScreen] = useState(false)
  const [isMobileDevice, setIsMobileDevice] = useState(false)
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
      setIsMobileDevice(mobileKeywords.some(keyword => userAgent.includes(keyword)));
      setIsSmallScreen(window.innerWidth <= 768); // Adjust this value as needed for your definition of a small screen
      const isLandscape = window.innerWidth > window.innerHeight;
      setIsMobile(isMobileDevice || isSmallScreen);
      setIsLandscape(isLandscape);
      if (isMobileDevice || isSmallScreen) {
        document.body.classList.toggle('mobile', isMobileDevice || isSmallScreen);
        document.body.classList.toggle('landscape', isLandscape);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    window.addEventListener('orientationchange', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
      window.removeEventListener('orientationchange', checkIfMobile);
    };
  }, [isMobileDevice, isSmallScreen]);

  useEffect(() => {
    dispatch(getScrollers());
  }, [dispatch]);

  return (
    <div className="App">
      <ResponsiveMenu isMobile={isMobile} />
      <Outlet context={{ isMobile, isLandscape, isMobileDevice, isSmallScreen }} />
    </div>
  );
}

export default App;